// URL State Management utilities
// Handles persisting application state in URL parameters for session persistence

export interface URLState {
  selectedIdeaId?: string;
  selectedTaskId?: string;
  view?: string;
  analysisState?: 'analyzing' | 'completed' | null;
  analysisType?: 'standard' | 'deep' | null;
}

// Get current URL state
export const getURLState = (): URLState => {
  const params = new URLSearchParams(window.location.search);
  return {
    selectedIdeaId: params.get('selectedIdeaId') || undefined,
    selectedTaskId: params.get('selectedTaskId') || undefined,
    view: params.get('view') || undefined,
    analysisState: (params.get('analysisState') as 'analyzing' | 'completed') || null,
    analysisType: (params.get('analysisType') as 'standard' | 'deep') || null,
  };
};

// Update URL state without triggering navigation
export const updateURLState = (updates: Partial<URLState>) => {
  const currentParams = new URLSearchParams(window.location.search);
  const currentHash = window.location.hash;

  // Update parameters
  Object.entries(updates).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      currentParams.delete(key);
    } else {
      currentParams.set(key, value.toString());
    }
  });

  // Build new URL
  const newSearch = currentParams.toString();
  const newURL = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${currentHash}`;

  // Update URL without triggering navigation
  window.history.replaceState(null, '', newURL);
};

// Clear specific URL state parameters
export const clearURLState = (keys: (keyof URLState)[]) => {
  const updates: Partial<URLState> = {};
  keys.forEach(key => {
    updates[key] = undefined;
  });
  updateURLState(updates);
};

// Clear all URL state
export const clearAllURLState = () => {
  const newURL = `${window.location.pathname}${window.location.hash}`;
  window.history.replaceState(null, '', newURL);
};

// Listen for URL changes (back/forward buttons)
export const onURLStateChange = (callback: (state: URLState) => void) => {
  const handlePopState = () => {
    callback(getURLState());
  };

  window.addEventListener('popstate', handlePopState);
  return () => window.removeEventListener('popstate', handlePopState);
}; 